import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Endpoint } from '@/Config/api/endpoint';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { KnowledgeBaseApi } from '@/Domain/api/KnowledgeBase/KnowledgeBase.api';
import {
  ArticleDto,
  ParagraphDto,
  ParagraphInputDto,
} from '@/Domain/api/KnowledgeBase/dto/Article.dto';

export const useCreateParagraph = () => {
  const { t } = useTranslation('paragraphs');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [Endpoint.knowledgeBase.articles.create],
    mutationFn: KnowledgeBaseApi.createArticle,
    onSuccess: (paragraph) => {
      queryClient.setQueriesData(
        { queryKey: [Endpoint.knowledgeBase.paragraphs] },
        (
          prevData:
            | InfiniteData<{ items: (ParagraphDto | ArticleDto)[]; count: number }>
            | undefined,
        ) => {
          if (!prevData) return prevData;

          return {
            ...prevData,
            pages: [
              {
                ...prevData.pages[0],
                items: [paragraph, ...prevData.pages[0].items],
              },
              ...prevData.pages.slice(1),
            ],
          };
        },
      );

      toast.success(t('toast.create'));
    },
    onError: (error: AxiosError<ErrorResponse>) =>
      toast.error(error.response?.data.errors.root[0].message),
  });

  const handleCreateParagraph = (data: ParagraphInputDto) => mutation.mutateAsync(data);

  return { handleCreateParagraph, isPending: mutation.isPending };
};
